import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import PitchCard from "../components/PitchCard";
import RecoveryPanel from "../components/RecoveryPanel";
import Badge from "../components/ui/Badge";
import Button from "../components/ui/Button";
import Card from "../components/ui/Card";
import Chip from "../components/ui/Chip";
import EmptyState from "../components/ui/EmptyState";
import LoadingState from "../components/ui/LoadingState";
import { fetchGuestRecommendations } from "../api/client";
import { BETA_ANALYTICS_EVENTS, trackBetaEvent } from "../utils/analytics";
import {
  clearGuestConversionState,
  clearProfile,
  isValidProfile,
  loadProfile,
  saveGuestConversionState,
} from "../utils/storage";
import { getCurrentUser } from "../utils/auth";

const RECOMMENDATION_RECOVERY = {
  missing_profile: {
    title: "Quiz answers needed",
    message: "Take the quiz first so recommendations use your real picks.",
    variant: "warning",
  },
  empty_success: {
    title: "No matches came back",
    message: "Your quiz loaded correctly, but this run did not return matches. Try broadening a few picks.",
    variant: "warning",
  },
  malformed_response: {
    title: "Recommendations could not be read",
    message: "The server responded, but the results were not in the expected shape.",
    variant: "error",
  },
  backend_failure: {
    title: "Recommendations did not reload",
    message: "Your saved quiz profile is still here. Retry the request or retake the quiz.",
    variant: "error",
  },
};

const RECOVERY_TIMEOUT_MS = 15000;
const RECOVERY_TIMEOUT_MESSAGE = "Recommendation recovery timed out";

function withRecoveryTimeout(requestPromise) {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = window.setTimeout(() => {
      reject(new Error(RECOVERY_TIMEOUT_MESSAGE));
    }, RECOVERY_TIMEOUT_MS);
  });

  return Promise.race([requestPromise, timeoutPromise]).finally(() => {
    window.clearTimeout(timeoutId);
  });
}

export default function Recommendations() {
  const location = useLocation();
  const navigate = useNavigate();
  const lastRecoveryRequest = useRef(null);

  const [shows, setShows] = useState([]);
  const [fromGuestQuiz, setFromGuestQuiz] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [isRecovering, setIsRecovering] = useState(false);
  const [recoveryState, setRecoveryState] = useState(null);
  const [recoveryAttempt, setRecoveryAttempt] = useState(0);

  useEffect(() => {
    let active = true;

    getCurrentUser()
      .then((user) => {
        if (active) setCurrentUser(user);
      })
      .catch(() => {
        if (active) setCurrentUser(null);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const incomingResults = location.state?.results;
    const hasIncomingResults = Object.prototype.hasOwnProperty.call(
      location.state || {},
      "results"
    );
    const incomingProfile = location.state?.userProfile || loadProfile() || null;

    if (hasIncomingResults) {
      setFromGuestQuiz(true);
      setUserProfile(incomingProfile);
      setIsRecovering(false);

      if (!Array.isArray(incomingResults)) {
        setShows([]);
        setRecoveryState("malformed_response");
        return;
      }

      setShows(incomingResults);
      setRecoveryState(incomingResults.length === 0 ? "empty_success" : null);
      return;
    }

    if (!isValidProfile(incomingProfile)) {
      setShows([]);
      setFromGuestQuiz(false);
      setUserProfile(null);
      setIsRecovering(false);
      setRecoveryState("missing_profile");
      return;
    }

    const requestKey = `${JSON.stringify(incomingProfile)}:${recoveryAttempt}`;
    if (lastRecoveryRequest.current === requestKey) return;
    lastRecoveryRequest.current = requestKey;

    let cancelled = false;
    setShows([]);
    setFromGuestQuiz(true);
    setUserProfile(incomingProfile);
    setIsRecovering(true);
    setRecoveryState(null);

    const recoverRecommendations = async () => {
      try {
        const res = await withRecoveryTimeout(
          Promise.resolve().then(() => fetchGuestRecommendations(incomingProfile))
        );

        if (cancelled) return;
        const recoveredResults = res?.data?.results;
        if (!Array.isArray(recoveredResults)) {
          throw new Error("Recommendation response was not usable");
        }
        setShows(recoveredResults);
        setRecoveryState(recoveredResults.length === 0 ? "empty_success" : null);
        trackBetaEvent(BETA_ANALYTICS_EVENTS.RECOMMENDATION_GENERATED, {
          authenticated: Boolean(incomingProfile?.user_id),
          result_count: recoveredResults.length,
          source: "profile_restore",
        });
      } catch (err) {
        if (cancelled) return;
        console.error("❌ Recommendation recovery failed:", err);
        setShows([]);
        setFromGuestQuiz(false);
        setRecoveryState(
          err.message === "Recommendation response was not usable"
            ? "malformed_response"
            : "backend_failure"
        );
      } finally {
        if (!cancelled) setIsRecovering(false);
      }
    };

    recoverRecommendations();

    return () => {
      cancelled = true;
      if (lastRecoveryRequest.current === requestKey) {
        lastRecoveryRequest.current = null;
      }
    };
  }, [location.state, recoveryAttempt]);

  useEffect(() => {
    if (!currentUser && shows.length > 0 && isValidProfile(userProfile)) {
      saveGuestConversionState({ profile: userProfile, results: shows });
    }
  }, [currentUser, shows, userProfile]);

  const resolvedUserId = currentUser?.id || userProfile?.user_id || null;

  const guestId = useMemo(() => {
    if (!shows.length || resolvedUserId) return null;
    return userProfile?.guest_id || null;
  }, [shows.length, resolvedUserId, userProfile]);

  const conversionState = useMemo(
    () => ({
      convertGuest: true,
      guestProfile: userProfile,
      results: shows,
    }),
    [shows, userProfile]
  );

  const handleRetakeQuiz = () => {
    clearProfile();
    clearGuestConversionState();
    navigate("/quiz");
  };

  const handleRetryRecovery = () => {
    if (!isValidProfile(userProfile || loadProfile())) {
      setRecoveryState("missing_profile");
      return;
    }

    lastRecoveryRequest.current = null;
    setRecoveryAttempt((attempt) => attempt + 1);
  };

  const hasRecommendations = shows.length > 0;
  const recoveryCopy = recoveryState ? RECOMMENDATION_RECOVERY[recoveryState] : null;
  const recoveryActions =
    recoveryState === "backend_failure" || recoveryState === "malformed_response"
      ? [
          {
            label: "Retry",
            onClick: handleRetryRecovery,
            disabled: isRecovering,
          },
          {
            label: "Retake Quiz",
            onClick: handleRetakeQuiz,
          },
        ]
      : [
          {
            label: "Take the Quiz",
            onClick: handleRetakeQuiz,
          },
        ];

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-8 grid gap-6 lg:grid-cols-[1fr_280px] lg:items-end">
        <div>
          <Badge variant="gold" className="mb-4">Your vibe is locked</Badge>
          <h1 className="font-display text-4xl font-bold leading-tight text-hookd-cream sm:text-5xl">
            Your Empress Picks
          </h1>
          <p className="mt-3 max-w-2xl text-hookd-muted">
            Handpicked shows and movies based on your quiz energy.
          </p>
        </div>

        {hasRecommendations && fromGuestQuiz && (
          <Button
            type="button"
            onClick={handleRetakeQuiz}
            variant="outline"
            className="w-full lg:w-auto"
          >
            Retake Quiz
          </Button>
        )}
      </div>

      <div className="mb-8 flex flex-wrap gap-3">
        <Chip active>All {shows.length || ""}</Chip>
        <Chip>Movies</Chip>
        <Chip>Series</Chip>
        <Chip>Best Match</Chip>
      </div>

      {isRecovering && (
        <div className="mb-6">
          <LoadingState title="Restoring Your Matches" message="Reloading recommendations from your saved quiz profile." />
        </div>
      )}

      {!isRecovering && recoveryCopy && (
        <div className="mb-6">
          <RecoveryPanel
            title={recoveryCopy.title}
            message={recoveryCopy.message}
            variant={recoveryCopy.variant}
            actions={recoveryActions}
          />
        </div>
      )}

      {hasRecommendations && fromGuestQuiz && (
        <Card className="mb-6 p-5">
          <div className="grid gap-4 md:grid-cols-[1fr_auto] md:items-center">
            <div>
              <h2 className="font-display text-2xl font-bold text-hookd-cream">
                Vibe Locked & Crowned
              </h2>
              <p className="mt-1 text-sm text-hookd-muted">
                Based on your energy, these shows were handpicked for you.
              </p>
            </div>
            {userProfile && (
              <div className="flex flex-wrap gap-2 md:justify-end">
                {(userProfile.favorite_genres || []).slice(0, 3).map((genre) => (
                  <Badge key={genre}>{genre}</Badge>
                ))}
                {userProfile.vibe_pref && <Badge variant="gold">{userProfile.vibe_pref} vibe</Badge>}
              </div>
            )}
          </div>
        </Card>
      )}

      {hasRecommendations && fromGuestQuiz && !currentUser && isValidProfile(userProfile) && (
        <Card className="mb-6 p-5">
          <h3 className="font-display text-2xl font-bold text-hookd-cream">Love your recs?</h3>
          <p className="mt-2 text-sm text-hookd-muted mb-4">
            Create an account or sign in to keep this quiz and recommendation history.
          </p>
          <div className="grid gap-3 sm:flex sm:flex-wrap">
            <Button
              as={Link}
              to="/signup"
              state={conversionState}
              className="w-full sm:w-auto"
            >
              Create Account
            </Button>
            <Button
              as={Link}
              to="/login"
              state={conversionState}
              variant="outline"
              className="w-full sm:w-auto"
            >
              Sign In
            </Button>
          </div>
        </Card>
      )}

      <div className="grid gap-5 lg:grid-cols-[1fr_280px]">
        <div className="grid gap-5">
        {hasRecommendations ? (
          shows.map((show) => (
            <PitchCard
              key={show.title}
              show={show}
              guestId={guestId}
              userId={resolvedUserId}
            />
          ))
        ) : !isRecovering && !recoveryCopy ? (
          <EmptyState
            title="No recommendations loaded yet"
            message="Take the quiz first so I can crown your next binge properly."
            primaryAction={{ label: "Take the Quiz", to: "/quiz" }}
            secondaryAction={{ label: "Reset Quiz State", onClick: handleRetakeQuiz }}
          />
        ) : null}
        </div>

        {hasRecommendations && (
          <aside className="grid content-start gap-5">
            <Card className="p-5">
              <h2 className="font-display text-2xl font-bold text-hookd-cream">
                Your Vibe Summary
              </h2>
              <dl className="mt-5 space-y-4 text-sm">
                <div>
                  <dt className="font-bold text-hookd-gold">Energy</dt>
                  <dd className="text-hookd-muted">{(userProfile?.favorite_moods || []).slice(0, 3).join(", ") || "Not set"}</dd>
                </div>
                <div>
                  <dt className="font-bold text-hookd-gold">Genres</dt>
                  <dd className="text-hookd-muted">{(userProfile?.favorite_genres || []).slice(0, 3).join(", ") || "Not set"}</dd>
                </div>
                <div>
                  <dt className="font-bold text-hookd-gold">Commitment</dt>
                  <dd className="text-hookd-muted">{userProfile?.commitment_pref || (userProfile?.commitment_flexible ? "Open-ended" : "Not set")}</dd>
                </div>
              </dl>
            </Card>

            <Card className="p-5">
              <h2 className="font-display text-2xl font-bold text-hookd-cream">
                Not quite right?
              </h2>
              <p className="mt-2 text-sm leading-6 text-hookd-muted">
                Retake the quiz or use Guest Mode for a different run.
              </p>
              <div className="mt-5 grid gap-3">
                <Button variant="outline" onClick={handleRetakeQuiz}>Retake Quiz</Button>
                <Button as={Link} to="/guest-mode" variant="secondary">Guest Mode</Button>
              </div>
            </Card>
          </aside>
        )}
      </div>
    </div>
  );
}
